/**
 * Banner globale che appare in cima ad ogni pagina quando la subscription
 * è in stato `cancelled` o `past_due`.
 *
 * Comunica chiaramente:
 *  - L'app è in **sola lettura** — l'utente vede tutti i suoi dati ma non
 *    può creare/modificare/eliminare nulla (gate vero a livello DB via RLS).
 *  - Per riprendere serve riattivare l'abbonamento → CTA che porta a billing.
 *
 * Non-dismissible: l'utente NON deve poterlo chiudere (non è una notifica
 * informativa, è uno stato bloccante del sistema).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertOctagon, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCanWrite } from '@/hooks/useCanWrite';

const SubscriptionBlockedBanner: React.FC = () => {
  const { blocked, plan, blockedReason } = useCanWrite();
  const navigate = useNavigate();

  if (!blocked) return null;

  const title =
    plan === 'past_due'
      ? 'Pagamento sospeso'
      : plan === 'trial_expired'
        ? 'Prova gratuita terminata · App in sola lettura'
        : 'Abbonamento scaduto · App in sola lettura';

  return (
    <div className="sticky top-0 z-[60] w-full bg-red-600 text-white shadow-md">
      <div className="w-full flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <AlertOctagon className="h-5 w-5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-sm leading-tight">{title}</div>
            <div className="text-xs text-red-50 leading-tight truncate">
              {blockedReason} I tuoi dati restano accessibili ma le scritture sono disabilitate.
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="bg-white text-red-700 hover:bg-red-50 flex-shrink-0 font-semibold"
          onClick={() => navigate('/settings/billing')}
        >
          Riattiva
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionBlockedBanner;
